/**
 * Константы для страницы детального просмотра посадки
 */
import i18n from '../../../i18n';

/** Label of a code from a `plantings:<group>.<code>` map, resolved at call time. */
const label = (group: string, code: string | number | null | undefined): string | undefined => {
  if (code === null || code === undefined || code === '') return undefined;
  const key = `plantings:${group}.${code}`;
  return i18n.exists(key) ? String(i18n.t(key)) : undefined;
};

/** Коды событий, влияющие на количество растений в посадке */
export const QUANTITY_AFFECTING_KINDS = new Set([
  'sold', 'written_off', 'disposed', 'partial_sale', 'partial_writeoff',
]);

/** Маппинг kind-кодов на человекочитаемые названия для старых событий без шаблона */
export const kindName = (kind: string | null | undefined): string | undefined => label('eventKind', kind);

/** Технические свойства, скрытые из пользовательского интерфейса */
export const TECHNICAL_PROPERTY_CODES = new Set([
  'planting_event_kind',
  'quantity_affected',
  'planting_old_status',
  'planting_new_status',
  'planting_status_reason',
  'planting_location_payload',
  'transplanting',
]);

/** Метрики для графика прогноза симуляции (labelKey/unitKey: namespace plantings) */
export const FORECAST_METRICS = [
  { key: 'harvestStandingKg', labelKey: 'forecast.metric.harvestStandingKg', color: '#389e0d', unitKey: 'units.kgPerPlant' },
  { key: 'tempAvgC', labelKey: 'forecast.metric.tempAvgC', color: '#f5222d', unitKey: 'units.celsius' },
  { key: 'totalDliMol', labelKey: 'forecast.metric.totalDliMol', color: '#d48806', unitKey: 'units.molPerM2Day' },
  { key: 'astronomicalDaylightHours', labelKey: 'forecast.metric.astronomicalDaylightHours', color: '#faad14', unitKey: 'units.hoursPerDay' },
  { key: 'outdoorSunHours', labelKey: 'forecast.metric.outdoorSunHours', color: '#d4b106', unitKey: 'units.hoursPerDay' },
  { key: 'photoperiodHours', labelKey: 'forecast.metric.photoperiodHours', color: '#ad6800', unitKey: 'units.hoursPerDay' },
  { key: 'dimensions.height', labelKey: 'forecast.metric.height', color: '#1677ff', unitKey: 'units.cm' },
  { key: 'etiolation.stemThickness', labelKey: 'forecast.metric.stemThickness', color: '#722ed1', unitKey: 'units.index' },
  { key: 'etiolation.stress', labelKey: 'forecast.metric.etiolationStress', color: '#eb2f96', unitKey: 'units.index' },
  { key: 'dimensions.trunkDiameter', labelKey: 'forecast.metric.trunkDiameter', color: '#13c2c2', unitKey: 'units.cm' },
  { key: 'dimensions.crownDiameter', labelKey: 'forecast.metric.crownDiameter', color: '#2f54eb', unitKey: 'units.cm' },
  { key: 'healthScore', labelKey: 'forecast.metric.healthScore', color: '#52c41a', unitKey: 'units.index' },
  { key: 'soilMoisturePct', labelKey: 'forecast.metric.soilMoisturePct', color: '#8c8c8c', unitKey: 'units.percent' },
  { key: 'stressMultiplier', labelKey: 'forecast.metric.stressMultiplier', color: '#fa8c16', unitKey: 'units.index' },
  { key: 'rotationFactor', labelKey: 'forecast.metric.rotationFactor', color: '#a0522d', unitKey: 'units.index' },
  { key: 'gdd', labelKey: 'forecast.metric.gdd', color: '#722ed1', unitKey: 'units.degreeDay' },
  { key: 'currentQuantity', labelKey: 'forecast.metric.currentQuantity', color: '#531dab', unitKey: 'units.pcs', isBatchOnly: true },
  { key: 'stuntFactor', labelKey: 'forecast.metric.stuntFactor', color: '#c41d7f', unitKey: 'units.index' },
] as const;

export type ForecastMetricKey = typeof FORECAST_METRICS[number]['key'];

/** Серии для столбчатой диаграммы ежедневного урожая */
export const HARVEST_DAILY_CHART_SERIES = [
  { key: 'harvestPotentialDailyKg', labelKey: 'forecast.dailySeries.harvestPotentialDailyKg', color: '#389e0d' },
  { key: 'harvestCollectedTodayKg', labelKey: 'forecast.dailySeries.harvestCollectedTodayKg', color: '#0958d9' },
  { key: 'harvestLostTodayKg', labelKey: 'forecast.dailySeries.harvestLostTodayKg', color: '#cf1322' },
] as const;

/** Запрашиваемые свойства для ряда временных данных симуляции */
export const TIMESERIES_PROPERTIES = [
  'ageDays', 'ageYears', 'ageDisplayUnit', 'ageDisplayValue', 'isAnnualLike',
  'tempMinC', 'tempMaxC', 'tempAvgC',
  'astronomicalDaylightHours', 'outdoorSunHours', 'cloudCover', 'solarPpfdUmol',
  'photoperiodHours', 'lampExtensionHours', 'supplementalLightHours',
  'totalDliMol', 'naturalDliMol', 'supplementalDliMol',
  'dliMinMol', 'dliTargetMol', 'photoperiodTargetHours',
  'recommendedLampHours', 'recommendedLampPpfdUmol',
  'recommendedLampPowerWm2', 'recommendedLampPowerTotalW',
  'dimensions.height', 'dimensions.trunkDiameter', 'dimensions.crownDiameter',
  'etiolation.stress', 'etiolation.stemThickness', 'etiolation.heightGrowthMultiplier',
  'healthScore', 'stressMultiplier', 'stressFactors',
  'soilMoisturePct', 'rotationFactor', 'rotationReasons', 'rotationState',
  'gdd', 'vegetationStage',
  'harvestReady', 'harvestReadinessPct',
  'harvestPotentialDailyKg', 'harvestPotentialCumulativeKg', 'harvestPotentialRemainingKg',
  'harvestProjectedTotalKg', 'harvestAvailableKg',
  'fruitSummary', 'fruitReferencePlantCount', 'harvestStandingKg',
  'fruitGreenKg', 'fruitTurningKg', 'fruitRipeKg', 'fruitOverripeKg',
  'harvestCollectedTodayKg', 'harvestCollectedCumulativeKg',
  'harvestLostTodayKg', 'harvestLostCumulativeKg',
  'lifecycleType', 'lifecyclePhase', 'lifecycleYieldMultiplier', 'lifecycleProgress',
  'bbchCode', 'bbchCodeText', 'bbchPrincipalStage', 'bbchPrincipalLabel',
  'bbchLabel', 'bbchConfidence', 'bbchCropGroup', 'bbchStatus',
  'bbchScale', 'bbchScaleVersion', 'bbchRange',
  'currentQuantity', 'isDead', 'stuntFactor',
];

/** Человекочитаемые названия фаз жизненного цикла */
export const lifecyclePhaseLabel = (phase: string | null | undefined): string | undefined => label('lifecyclePhase', phase);

/** Map-shaped view of `lifecyclePhaseLabel` for existing callers; resolves on each access. */
export const LIFECYCLE_PHASE_LABELS: Record<string, string> = new Proxy({} as Record<string, string>, {
  get: (_target, prop) => (typeof prop === 'string' ? lifecyclePhaseLabel(prop) : undefined),
});

/** Человекочитаемые названия принципиальных стадий BBCH (0..9) */
export const bbchPrincipalLabel = (principal: number | null | undefined): string | undefined => label('bbchPrincipal', principal);

/** Человекочитаемые названия групп BBCH (cropGroup) */
export const bbchCropGroupLabel = (group: string | null | undefined): string | undefined => label('bbchCropGroup', group);

/** Человекочитаемые названия уровня доверия BBCH-маппинга */
export const bbchConfidenceLabel = (confidence: string | null | undefined): string | undefined => label('bbchConfidence', confidence);

/** Человекочитаемые названия lifecycle gate predicates */
export const lifecyclePredicateLabel = (predicate: string | null | undefined): string | undefined => label('lifecyclePredicate', predicate);

/** Читаемые подписи стресс-факторов */
export const stressFactorLabel = (factor: string | null | undefined): string | undefined => label('stressFactor', factor);

/** Названия болезней/вредителей */
export const diseaseName = (code: string | null | undefined): string | undefined => {
  if (!code) return undefined;
  const key = `sim:disease.${code}.name`;
  return i18n.exists(key) ? String(i18n.t(key)) : undefined;
};

/** Коды вредителей (для выбора иконки) */
export const PEST_CODES = new Set(['aphids', 'whitefly', 'spider_mite']);

/** Читаемые подписи к причинам ротации */
export const rotationReasonLabel = (reason: string | null | undefined): string | undefined => label('rotationReason', reason);

/** Only engine reason codes explain soil interactions; the factor alone does not imply bad neighbors. */
export const soilInteractionReasonLabels = (reasons: unknown): string[] => {
  if (!Array.isArray(reasons)) return [];
  return reasons.map(reason => typeof reason === 'string' ? reason : reason?.code)
    .filter((code): code is string => typeof code === 'string')
    .map(code => label('soilInteractionReason', code)).filter((text): text is string => Boolean(text));
};
